
import { Button } from '@repo/ui/components/button';
import { ArrowLeft, ArrowRight } from '@repo/ui/components/icon';

import { cn } from '@repo/ui/lib/utils';

type PageNumber = number | '...';

function getPageNumbers(currentPage: number, totalPages: number): PageNumber[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, idx) => idx + 1);
  }

  const pages: PageNumber[] = [];
  const siblings = 1;
  const leftSibling = Math.max(currentPage - siblings, 2);
  const rightSibling = Math.min(currentPage + siblings, totalPages - 1);

  pages.push(1);

  if (leftSibling > 2) {
    pages.push('...');
  }

  for (let page = leftSibling; page <= rightSibling; page += 1) {
    pages.push(page);
  }

  if (rightSibling < totalPages - 1) {
    pages.push('...');
  }

  pages.push(totalPages);
  return pages;
}

type PaginationProps = {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  pageSizeOptions?: number[];
  totalItems?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  className?: string;
};

export default function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  className,
}: PaginationProps) {
  if (totalPages <= 1) {
    return null;
  }

  const pageNumbers = getPageNumbers(currentPage, totalPages);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    onPageChange(page);
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-4 border-t border-gray-200 py-[60px] md:flex-row md:items-center md:justify-between",
        className
      )}
    >
      <div className="flex items-center w-full gap-2">
        <Button
          variant="outline"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <span className="sr-only">Go to previous page</span>
          <ArrowLeft />
          Trang trước
        </Button>

        <nav
          aria-label="Pagination"
          className="flex items-center justify-center flex-1 gap-2"
        >
          {pageNumbers.map((page, idx) =>
            page === "..." ? (
              <span
                key={`ellipsis-${idx}`}
                className="px-2 text-sm text-gray-400"
              >
                ...
              </span>
            ) : (
              <Button
                key={page}
                variant={page === currentPage ? "default" : "outline"}
                className={cn(
                  "h-9 min-w-9 px-2",
                  page === currentPage &&
                    "bg-primary text-white hover:bg-primary"
                )}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </Button>
            )
          )}
        </nav>

        <Button
          variant="outline"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <span className="sr-only">Go to next page</span>
          Trang tiếp theo <ArrowRight />
        </Button>
      </div>
    </div>
  );
}

export { getPageNumbers };
